import { useEffect, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { Mail, MailOpen } from 'lucide-react'
import { toast } from 'sonner'

import { contact, type ContactSubmission } from '../api/endpoints'
import { Button } from '../components/Button'
import { DataTable } from '../components/DataTable'
import { PageLoader } from '../components/Spinner'
import { timeAgo } from '../lib/format'

export function ContactPage() {
  const [data, setData] = useState<ContactSubmission[]>([])
  const [loading, setLoading] = useState(true)
  const [unreadOnly, setUnreadOnly] = useState(false)

  function reload() {
    setLoading(true)
    contact
      .list(unreadOnly ? { isRead: false } : {})
      .then((r) => setData(r.submissions))
      .catch((err) => toast.error('Failed to load', { description: err.message }))
      .finally(() => setLoading(false))
  }

  useEffect(reload, [unreadOnly])

  async function markRead(sub: ContactSubmission) {
    if (sub.isRead) return
    try {
      await contact.markRead(sub.id)
      toast.success('Marked as read')
      reload()
    } catch (err) {
      toast.error('Failed', { description: (err as Error).message })
    }
  }

  const columns: ColumnDef<ContactSubmission>[] = [
    {
      header: '',
      accessorKey: 'isRead',
      size: 40,
      cell: ({ row }) => (
        <button onClick={() => markRead(row.original)} title={row.original.isRead ? 'Read' : 'Mark as read'}>
          {row.original.isRead ? <MailOpen className="size-4 text-slatey" /> : <Mail className="size-4 text-forest" />}
        </button>
      ),
    },
    { header: 'Name', accessorKey: 'name', cell: ({ getValue }) => <span className="font-medium">{String(getValue())}</span> },
    { header: 'Email', accessorKey: 'email', cell: ({ getValue }) => <span className="text-sm text-slatey">{String(getValue())}</span> },
    { header: 'Subject', accessorKey: 'subject', cell: ({ getValue }) => <span className="text-sm">{String(getValue())}</span> },
    {
      header: 'Message',
      accessorKey: 'message',
      cell: ({ getValue }) => <p className="text-sm text-slatey line-clamp-2 max-w-xs">{String(getValue())}</p>,
    },
    { header: 'Received', accessorKey: 'createdAt', cell: ({ getValue }) => <span className="text-xs text-slatey">{timeAgo(getValue() as string)}</span> },
  ]

  if (loading) return <PageLoader />

  return (
    <div className="space-y-6 max-w-7xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-ink">Contact Submissions</h1>
          <p className="text-sm text-slatey mt-1">{data.length} message{data.length === 1 ? '' : 's'}</p>
        </div>
        <Button variant={unreadOnly ? 'default' : 'outline'} onClick={() => setUnreadOnly(!unreadOnly)}>
          {unreadOnly ? 'Show All' : 'Unread Only'}
        </Button>
      </div>

      <DataTable
        data={data} columns={columns}
        emptyTitle="No messages yet"
        emptyDescription="Customer contact form submissions will appear here."
      />
    </div>
  )
}
